import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: October 14, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Invy ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
            This privacy policy explains how we collect, use, and safeguard your information when you use our inventory management system.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> When you sign in using OAuth (Google, Microsoft, or Apple), we receive your name, email address, and profile information from your chosen provider.</li>
            <li><strong>Inventory Data:</strong> Information you enter about your locations, containers, and items including names, descriptions, barcodes, and quantities.</li>
            <li><strong>Product Information:</strong> When you scan barcodes, we may retrieve product information from third-party databases (UPC Item DB) to help auto-fill product details.</li>
            <li><strong>Images:</strong> Product images that are automatically downloaded and stored when you scan barcodes.</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Data:</strong> Information about how you interact with our service, including pages visited and features used.</li>
            <li><strong>Device Information:</strong> Browser type, IP address, and device identifiers.</li>
            <li><strong>Cookies:</strong> We use essential cookies to maintain your session and authentication status.</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Invy service</li>
            <li>Authenticate your account and maintain your session</li>
            <li>Store and manage your inventory data</li>
            <li>Auto-fill product information when scanning barcodes</li>
            <li>Improve and optimize our service</li>
            <li>Communicate with you about service updates or issues</li>
            <li>Ensure security and prevent fraud</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          <h3>4.1 Where We Store Your Data</h3>
          <ul>
            <li><strong>Database:</strong> Your inventory data is stored in Cloudflare D1 (a serverless SQL database)</li>
            <li><strong>Images:</strong> Product images are stored in Cloudflare R2 (object storage)</li>
            <li><strong>Processing:</strong> All data is processed through Cloudflare Workers at edge locations globally</li>
          </ul>

          <h3>4.2 Security Measures</h3>
          <ul>
            <li>Industry-standard encryption for data in transit (HTTPS/TLS)</li>
            <li>Secure authentication using OAuth 2.0 and JWT tokens</li>
            <li>Rate limiting to prevent abuse</li>
            <li>Regular security updates and monitoring</li>
            <li>User data isolation - you can only access your own data</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Sharing and Third Parties</h2>
          <h3>5.1 We Share Data With:</h3>
          <ul>
            <li><strong>OAuth Providers:</strong> Google, Microsoft, or Apple (only during authentication)</li>
            <li><strong>UPC Item DB:</strong> When you scan barcodes, we query their API to retrieve product information</li>
            <li><strong>Cloudflare:</strong> Our infrastructure provider for hosting and data storage</li>
          </ul>

          <h3>5.2 We Do NOT:</h3>
          <ul>
            <li>Sell your personal information to third parties</li>
            <li>Share your inventory data with other users or companies</li>
            <li>Use your data for advertising purposes</li>
            <li>Track you across other websites or services</li>
          </ul>
        </section>

        <section>
          <h2>6. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct your information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
            <li><strong>Export:</strong> Download your inventory data</li>
            <li><strong>Opt-out:</strong> Disable automatic barcode lookup if you prefer manual entry</li>
          </ul>
          <p>
            To exercise these rights, please contact us at <a href="mailto:privacy@invy.me">privacy@invy.me</a>
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <ul>
            <li><strong>Active Accounts:</strong> We retain your data as long as your account is active</li>
            <li><strong>Deleted Accounts:</strong> Data is permanently deleted within 30 days of account deletion</li>
            <li><strong>Session Data:</strong> Authentication sessions expire after 30 days of inactivity</li>
            <li><strong>Backups:</strong> Backup copies are retained for up to 90 days for disaster recovery</li>
          </ul>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>
            Invy is not intended for use by children under 13 years of age. We do not knowingly collect personal information from children under 13. 
            If you believe we have collected information from a child under 13, please contact us immediately.
          </p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>
            Your data is processed through Cloudflare's global network. Cloudflare operates data centers worldwide and may transfer 
            your data across borders to provide fast, reliable service. All transfers comply with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the new policy on this page</li>
            <li>Updating the "Last Updated" date</li>
            <li>Sending you an email notification (for significant changes)</li>
          </ul>
          <p>
            Your continued use of Invy after changes are posted constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>If you have questions or concerns about this privacy policy, please contact us:</p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:privacy@invy.me">privacy@invy.me</a></li>
            <li><strong>Support:</strong> <a href="mailto:support@invy.me">support@invy.me</a></li>
          </ul>
        </section>

        <section>
          <h2>12. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to know if personal information is sold or disclosed</li>
            <li>Right to opt-out of the sale of personal information (Note: We do not sell personal information)</li>
            <li>Right to deletion of personal information</li>
            <li>Right to non-discrimination for exercising CCPA rights</li>
          </ul>
        </section>

        <section>
          <h2>13. GDPR Compliance (European Users)</h2>
          <p>
            If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):
          </p>
          <ul>
            <li>Legal basis for processing: Consent and legitimate interests</li>
            <li>Right to access, rectification, erasure, and data portability</li>
            <li>Right to object to processing and restrict processing</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to lodge a complaint with a supervisory authority</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
